import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'default'
    | 'outline'
    | 'secondary'
    | 'destructive'
    | 'ghost'
    | 'pill'
    | 'outline-safe'
    | 'outline-warn'
    | 'outline-risk'
    | 'surface';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Only used by variant="pill" — selects the active/inactive tab-toggle color pair. */
  active?: boolean;
  /**
   * Only used by variant="pill" — the color family for the active/inactive
   * pair. `neutral` (default) is the standard tab-toggle look (ContextAnalyzer,
   * Tabs, SegmentedSwitch, RelevanceEnginePage, RelevanceEngineAB). The other
   * tones keep Explore's urlFilter chips' semantic match/no-match coloring.
   */
  tone?: 'neutral' | 'ink' | 'safe' | 'risk';
}

// Reproduces the rounded-full px-4 py-1.5 text-sm font-semibold transition-colors
// tab-toggle pattern repeated across ContextAnalyzer, Tabs, SegmentedSwitch,
// RelevanceEnginePage and RelevanceEngineAB — do not restyle without updating those.
const pillSizeStyles = {
  xs: 'px-2.5 py-0.5 text-xs',
  sm: 'px-3 py-1 text-xs',
  md: 'px-4 py-1.5 text-sm',
  lg: 'px-5 py-2 text-base'
};

const pillToneStyles = {
  neutral: {
    active: 'bg-surface text-accent-active shadow-sm',
    inactive: 'text-ink-muted hover:text-ink'
  },
  // Explore's urlFilter chips (bd-deepcontext-4n4m.3) — semantic tones so the
  // match/no-match color coding survives the move onto the shared pill.
  ink: {
    active: 'bg-ink text-canvas',
    inactive: 'bg-ghost-bg text-ink hover:opacity-80'
  },
  safe: {
    active: 'bg-nav-active text-safe ring-2 ring-accent',
    inactive: 'bg-safe-bg text-safe hover:opacity-80'
  },
  risk: {
    active: 'bg-nav-active text-risk ring-2 ring-accent',
    inactive: 'bg-risk-bg text-risk hover:opacity-80'
  }
};

/**
 * App-standard pill button. Variants map onto the epic design_html
 * component sheet: `default` = gradient primary CTA, `secondary` = solid
 * accent, `outline` = accent-ink outline, `ghost` = neutral ghost-bg,
 * `destructive` = danger, `pill` = rounded tab/mode-selector toggle.
 *
 * `outline-safe`/`outline-warn`/`outline-risk` (deepcontext-4n4m.4) — bordered
 * safe/warn/risk token outline with a tinted hover, for actions like
 * CategoryExclusionSelect's Include All/Exclude Sensitive/Exclude All that
 * have no color match among the accent-only variants above.
 * `surface` — bordered neutral surface button (PromptExport's Cancel/Export
 * PDF), distinct from `outline`'s accent border/text.
 */
export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'default',
  size = 'md',
  active = false,
  tone = 'neutral',
  className = '',
  ...props
}) => {
  if (variant === 'pill') {
    const toneStyles = pillToneStyles[tone];
    const pillStyles = `rounded-full ${pillSizeStyles[size]} font-semibold transition-colors ${active ? toneStyles.active : toneStyles.inactive}`;
    return (
      <button className={`${pillStyles} ${className}`} {...props}>
        {children}
      </button>
    );
  }

  const baseStyles = 'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-surface disabled:opacity-50 disabled:cursor-not-allowed';

  const variantStyles = {
    default: 'bg-gradient-accent text-white shadow-sm hover:opacity-90',
    secondary: 'bg-accent text-white hover:bg-accent-active',
    outline: 'bg-transparent border border-accent-active text-accent-active hover:bg-ghost-bg',
    ghost: 'bg-ghost-bg text-ink hover:opacity-80',
    destructive: 'bg-danger-500 text-white hover:opacity-90',
    'outline-safe': 'bg-transparent border border-safe text-safe hover:bg-safe-bg',
    'outline-warn': 'bg-transparent border border-warn text-warn hover:bg-warn-bg',
    'outline-risk': 'bg-transparent border border-risk text-risk hover:bg-risk-bg',
    surface: 'border border-line bg-surface text-ink hover:bg-ghost-bg'
  };

  // `md` is the row-facing size: fixed to the app's 42px row-control standard
  // (see _config/conventions.md) so a default-variant Button sits flush next
  // to SearchInput/Tabs/FilterSelect/Select in the same row. `xs` (inline/
  // table actions) and `lg` are not row controls and keep their own heights.
  const sizeStyles = {
    xs: 'px-2.5 py-1 text-xs',
    sm: 'px-4 py-1.5 text-xs',
    md: 'px-5 h-[42px] text-sm',
    lg: 'px-6 py-3 text-base'
  };

  return (
    <button
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};
